import { PluggableStorage } from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';

export type RedirectLoopPolicy = {
  name: string;
  threshold: number;
  timeframeMs: number;
  storageKey: string;
};

const storageKeyBase = `redirectTimestamps`; // 'navigateToUrl' is already in the base key, this is just the subkey for the redirect timestamps

export const REDIRECT_LOOP_POLICIES = {
  default: {
    name: 'default',
    threshold: 10,
    timeframeMs: 20000,
    storageKey: `${storageKeyBase}:default`,
  },
  noChallengeRecovery: {
    name: 'noChallengeRecovery',
    threshold: 1,
    timeframeMs: 5000,
    storageKey: `${storageKeyBase}:noChallengeRecovery`,
  },
} as const satisfies Record<string, RedirectLoopPolicy>;

export type RedirectLoopPolicyName = keyof typeof REDIRECT_LOOP_POLICIES;
export const DEFAULT_REDIRECT_LOOP_POLICY = REDIRECT_LOOP_POLICIES.default;

export const store = PluggableStorage.create('navigateToUrl', sessionStorage);

/**
 * Top level page navigation (as opposed to a react router based nav) with logging.
 * This should be the only place in gatekeeper that sets window.location.href.
 *
 * The auth flow is complex and there's potential to have too many redirects
 * or get stuck in a loop, so we log all top level navigations to help debug
 * any issues.
 */
export function navigateToUrl(
  url: string,
  opts?: {
    replace?: boolean;
    reason?: string;
    error?: Error | string | unknown;
    loopPolicy?: RedirectLoopPolicyName;
  }
) {
  const policy = resolveRedirectLoopPolicy(opts?.loopPolicy);
  const currentUrl = window.location.href;
  const currentUrlSanitized = sanitizeUrl(currentUrl);
  const nextUrlSanitized = sanitizeUrl(url);
  const logStr = `Top level navigation from ${currentUrlSanitized} to ${nextUrlSanitized}. Reason: ${
    opts?.reason ?? ''
  }`;
  console.log(logStr);
  sentry.addBreadcrumb({
    category: 'navigateToUrl',
    message: logStr,
  });

  // Infinite redirect check
  const { isInfiniteLoop, redirects } = isInfiniteRedirectLoop(url, policy);
  if (isInfiniteLoop) {
    console.error('Blocked navigation due to suspected infinite redirect loop.', { redirects, url, currentUrl });
    sentry.error({
      error: new Error('Blocked navigation due to suspected infinite redirect loop'),
      topic: 'auth',
      team: 'users-infra',
      sampleRate: 1,
      addContext: {
        name: 'navigateToUrl',
        context: {
          currentUrl: currentUrlSanitized,
          nextUrl: nextUrlSanitized,
          reason: opts?.reason,
          loopPolicy: policy.name,
          loopPolicyStorageKey: policy.storageKey,
          loopPolicyThreshold: policy.threshold,
          loopPolicyTimeframeMs: policy.timeframeMs,
          redirectTimestamps: redirects.map((r) => r.url).join(', '),
        },
      },
    });
    throw new Error('Blocked navigation due to suspected infinite redirect loop');
  }

  // This can be really high-volume — expected auth-flow recovery (expired challenge, network blip, etc.)
  if (opts?.error) {
    sentry.warn({
      error: formatErrorForSentry(opts.error),
      topic: 'auth',
      team: 'users-infra',
      addContext: {
        name: 'navigateToUrl',
        context: {
          currentUrl: currentUrlSanitized,
          nextUrl: nextUrlSanitized,
          reason: opts?.reason,
        },
      },
    });
  }

  if (opts?.replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

function resolveRedirectLoopPolicy(policyName: RedirectLoopPolicyName = 'default'): RedirectLoopPolicy {
  const policy = REDIRECT_LOOP_POLICIES[policyName];
  if (!policy) {
    throw new Error(`Unknown redirect loop policy: ${String(policyName)}`);
  }
  return policy;
}

function isInfiniteRedirectLoop(url: string, policy: RedirectLoopPolicy) {
  let redirects = store.getItem<{ timestamp: number; url: string }[]>(policy.storageKey, []);
  const now = Date.now();
  // Only keep timestamps from the policy window
  redirects = redirects.filter((redirect) => now - redirect.timestamp < policy.timeframeMs);
  const nextUrl = safeURL(url);
  redirects.push({ timestamp: now, url: nextUrl.host + nextUrl.pathname }); // store host+path only to avoid PII in logs
  store.setItem(policy.storageKey, redirects);

  const isInfiniteLoop = redirects.length > policy.threshold;
  if (isInfiniteLoop) {
    // clear this policy storage if we are blocking to avoid blocking again on next load
    store.removeItem(policy.storageKey);
  }

  return {
    isInfiniteLoop,
    redirects,
  };
}

function safeURL(url: string): URL {
  try {
    return new URL(url, window.location.origin);
  } catch (error) {
    return new URL('about:blank');
  }
}

/**
 * This helps with sidestepping getting PII auto-redacted in Sentry while still allowing us to log the URL structure for debugging purposes.
 * For logging only, will not result in a valid url.
 */
function sanitizeUrl(urlStr: string): string {
  const paramsToRedact = [
    'username',
    'login_challenge',
    'challenge',
    'logout_challenge',
    'logout_verifier',
    'consent_challenge',
    'id_token_hint',
    'state',
    'code',
  ];
  const redactionPlaceholder = '***';

  const url = safeURL(urlStr);
  url.searchParams.forEach((_, key) => {
    if (paramsToRedact.includes(key)) {
      url.searchParams.set(key, redactionPlaceholder);
    }
  });

  return url.toString();
}

function formatErrorForSentry(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
